/**
 * src/features/referral/ui/InviteWidget.tsx
 *
 * Feature component: invite link widget with URL copy, WhatsApp copy, and status summary.
 *
 * Props:
 *  - alwaysExpanded?: boolean — when true (Settings page), renders expanded without
 *    toggle button. When false/undefined (Dashboard), renders collapsed by default with
 *    a header button that toggles expansion.
 *
 * §10 optional complexity: InviteWidgetBody (which calls useReferralData) only mounts
 * when expanded, so collapsed Dashboard users never trigger GET /api/referral/me.
 *
 * T-27-04: All data rendered as React text content, no dangerouslySetInnerHTML.
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import { useReferralData } from '../hooks/useReferralData';
import { InviteUrlRow } from './InviteUrlRow';
import { WhatsAppRow } from './WhatsAppRow';
import { StatusList } from './StatusList';

// ---------------------------------------------------------------------------
// InviteWidgetBody — calls useReferralData; only mounts when expanded (§10)
// ---------------------------------------------------------------------------

const InviteWidgetBody: FC = () => {
  const { t } = useTranslation();
  const { data, loading, error } = useReferralData();

  if (error) {
    return (
      <p className="text-body text-destructive py-2">
        {t('referral.error', 'Could not load your invite link. Try again.')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4 pt-2">
      <InviteUrlRow url={data?.referralUrl ?? null} loading={loading} />
      <WhatsAppRow url={data?.referralUrl ?? null} />
      {data && <StatusList stats={data.stats} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// InviteWidget — main export
// ---------------------------------------------------------------------------

interface InviteWidgetProps {
  alwaysExpanded?: boolean;
}

export const InviteWidget: FC<InviteWidgetProps> = ({ alwaysExpanded = false }) => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [isExpanded, setIsExpanded] = useState(false);

  // Settings variant: always-expanded inline section, no toggle
  if (alwaysExpanded) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm">
        <h3 className="text-subhead font-bold text-slate-800 dark:text-slate-100 mb-4">
          {t('referral.section_title', 'Invite a colleague')}
        </h3>
        <InviteWidgetBody />
      </div>
    );
  }

  // Dashboard variant: collapsed by default, toggleable
  return (
    <div
      role="region"
      aria-label={t('referral.widget_title', 'Invite a colleague')}
      className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm mb-4"
    >
      {/* Header toggle row */}
      <button
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        className="flex items-center justify-between w-full min-h-[44px] px-4 py-3 rounded-2xl"
      >
        <span className="flex items-center gap-2 text-subhead font-bold text-slate-800 dark:text-slate-100">
          <Users size={18} className="text-brand-600 dark:text-brand-400" />
          {t('referral.invite_cta', 'Invite a colleague')}
        </span>
        {isExpanded ? (
          <ChevronUp size={16} className="text-slate-500 dark:text-slate-400" />
        ) : (
          <ChevronDown size={16} className="text-slate-500 dark:text-slate-400" />
        )}
      </button>

      {/* Expandable body — lazy mount (§10: fetch only fires when body mounts) */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            key="widget-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: reduced ? 0 : 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <InviteWidgetBody />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
